// Coin packs on the phone. The client only asks: Lagos Life's own sheet takes the payment (our own
// website's players pay through Stig Labs pay instead), and our server checks the purchase with the
// store before it gives a single coin. Anything a reload or a trip to the payment page interrupted
// is claimed again on the next start.
import { VatarClientError, type VatarPurchaseRef, vatar } from '@wahala/vatar-client';
import { useUi } from '../store/ui';
import { S } from '../strings';
import { ApiError, api } from './api';
import { parkNaira } from './money';

export const coins = (n: number) => new Intl.NumberFormat('en-GB').format(n);

function say(text: string | null, tone: 'done' | 'wahala' = 'wahala'): void {
  useUi.getState().set({ notice: text, noticeTone: tone });
}

function failure(e: unknown): string {
  const code = e instanceof ApiError || e instanceof VatarClientError ? e.code : 'generic';
  return S.errors[code] ?? S.errors.generic ?? '';
}

/** Hand one paid purchase to our server. Returns the coins it added (0 if it was already in). */
async function claim(ref: VatarPurchaseRef): Promise<number> {
  const { session } = await vatar.session();
  const r = await api.claim(session, ref.purchaseId);
  useUi.getState().set({ coins: r.coins });
  return r.granted;
}

/** Open the pay sheet for a coin pack, then claim it. */
export async function buyPack(productId: string): Promise<void> {
  say(null);
  try {
    const ref = await vatar.purchase(productId);
    const added = await claim(ref);
    if (added > 0) say(S.shop.added(parkNaira(added)), 'done');
  } catch (e) {
    say(failure(e));
  }
}

/** On start: claim anything paid but not delivered, and catch purchases confirmed while away. */
export async function restorePurchases(): Promise<void> {
  void vatar.onPurchase((ref) => {
    claim(ref)
      .then((added) => {
        if (added > 0) say(S.shop.added(parkNaira(added)), 'done');
      })
      .catch((e) => say(failure(e)));
  });
  const pending = await vatar.purchases();
  let added = 0;
  for (const ref of pending) {
    try {
      added += await claim(ref);
    } catch {
      // a refunded or foreign purchase: the server already said so; nothing to give
    }
  }
  if (added > 0) say(S.shop.added(parkNaira(added)), 'done');
}

// ---------------------------------------------------------------- our website's packs (Stig Labs pay)

/** References this phone sent to pay and has not heard settled: checked again on the next start. */
const OPEN_KEY = 'wahala.checkouts';

function openRefs(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(OPEN_KEY) ?? '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string').slice(-5) : [];
  } catch {
    return [];
  }
}

function keepRefs(refs: string[]): void {
  try {
    if (refs.length) localStorage.setItem(OPEN_KEY, JSON.stringify(refs.slice(-5)));
    else localStorage.removeItem(OPEN_KEY);
  } catch {
    // private window: the server's sweep still settles it
  }
}

function websiteFailure(e: unknown): string {
  const code = e instanceof ApiError ? e.code : 'generic';
  if (code === 'try_again') return S.shop.payTryAgain;
  return S.errors[code] ?? S.errors.generic ?? '';
}

/** Send the player to pay's checkout for a website pack; they come back to the play host. */
export async function checkoutPack(productId: string): Promise<void> {
  say(null);
  try {
    const { url, reference } = await api.checkout(productId);
    keepRefs([...openRefs(), reference]);
    location.assign(url);
  } catch (e) {
    say(websiteFailure(e));
  }
}

/**
 * On start (our website's players): settle the checkout pay just sent us back from, and any this
 * phone opened before. Pay adds ?reference=…&payment=…&status=… to our return address.
 */
export async function restoreCheckouts(): Promise<void> {
  const q = new URLSearchParams(location.search);
  const back = q.get('paid') === 'website' ? q.get('reference') : null;
  if (q.has('paid')) {
    for (const k of ['paid', 'reference', 'payment', 'status']) q.delete(k);
    const rest = q.toString();
    history.replaceState(history.state, '', `${location.pathname}${rest ? `?${rest}` : ''}${location.hash}`);
  }
  const refs = [...new Set([...openRefs(), ...(back ? [back] : [])])];
  if (!refs.length) return;
  if (back) say(S.shop.paying, 'done');
  let added = 0;
  const left: string[] = [];
  for (const ref of refs) {
    try {
      const r = await api.websitePurchase(ref);
      if (typeof r.coins === 'number') useUi.getState().set({ coins: r.coins });
      added += r.granted;
      if (r.status === 'pending') left.push(ref);
      if (ref === back && r.status !== 'paid') {
        say(
          r.status === 'pending' ? S.shop.payPending : r.status === 'refunded' ? S.shop.payRefunded : S.shop.payFailed,
        );
      }
    } catch (e) {
      // a reference the server never made, or another player's: forget it; anything else, try later
      if (!(e instanceof ApiError && (e.status === 403 || e.status === 404))) left.push(ref);
      if (ref === back) say(websiteFailure(e));
    }
  }
  keepRefs(left);
  if (added > 0) say(S.shop.added(parkNaira(added)), 'done');
  else if (back && !left.includes(back) && useUi.getState().notice === S.shop.paying) say(null);
}
